import type {
  AnalyticsEvent,
  AnalyticsImplementation,
  ConfigApi,
  DiscoveryApi,
  FetchApi,
} from '@backstage/frontend-plugin-api';

/** The shape POSTed to /api/analytics/events; the backend adds the (pseudonymised) user. */
export interface WireEvent {
  action: string;
  subject: string;
  value?: number;
  attributes?: Record<string, string | number | boolean>;
  context: { pluginId?: string; extensionId?: string };
}

export interface BackstageAnalyticsOptions {
  /** Flush at most this often while events trickle in. */
  flushIntervalMs?: number;
  /** Flush right away once this many events are queued (the backend accepts 50). */
  maxBatch?: number;
}

const MAX_SUBJECT = 512;
const MAX_ATTRIBUTE = 256;
const MAX_ATTRIBUTES = 20;

const noop: AnalyticsImplementation = { captureEvent: () => {} };

export function toWireEvent(event: AnalyticsEvent): WireEvent {
  // Query strings can carry search terms or ids; page views only need the path.
  const subject =
    event.action === 'navigate' ? event.subject.split('?')[0] : event.subject;
  const wire: WireEvent = {
    action: event.action.slice(0, 64),
    subject: subject.slice(0, MAX_SUBJECT),
    context: {
      pluginId: event.context.pluginId,
      extensionId: event.context.extensionId,
    },
  };
  if (typeof event.value === 'number' && Number.isFinite(event.value))
    wire.value = event.value;
  if (event.attributes) {
    const entries = Object.entries(event.attributes)
      .filter(([, v]) => typeof v !== 'number' || Number.isFinite(v))
      .slice(0, MAX_ATTRIBUTES)
      .map(
        ([k, v]) =>
          [k, typeof v === 'string' ? v.slice(0, MAX_ATTRIBUTE) : v] as const,
      );
    if (entries.length) wire.attributes = Object.fromEntries(entries);
  }
  return wire;
}

/**
 * Batches analytics events and sends them to our own analytics backend. Failures are dropped
 * silently: analytics must never break or slow down the portal.
 */
export class BackstageAnalytics implements AnalyticsImplementation {
  static fromConfig(
    config: ConfigApi,
    deps: { discoveryApi: DiscoveryApi; fetchApi: FetchApi },
    options?: BackstageAnalyticsOptions,
  ): AnalyticsImplementation {
    if (config.getOptionalBoolean('app.analytics.enabled') === false)
      return noop;
    return new BackstageAnalytics(deps.discoveryApi, deps.fetchApi, options);
  }

  private queue: WireEvent[] = [];
  private timer: ReturnType<typeof setTimeout> | undefined;
  private readonly flushIntervalMs: number;
  private readonly maxBatch: number;

  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
    options: BackstageAnalyticsOptions = {},
  ) {
    this.flushIntervalMs = options.flushIntervalMs ?? 10_000;
    this.maxBatch = Math.min(options.maxBatch ?? 25, 50);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden')
          void this.flush({ keepalive: true });
      });
    }
  }

  captureEvent(event: AnalyticsEvent): void {
    this.queue.push(toWireEvent(event));
    if (this.queue.length >= this.maxBatch) {
      void this.flush();
    } else if (!this.timer) {
      this.timer = setTimeout(() => void this.flush(), this.flushIntervalMs);
    }
  }

  /** Sends queued events; `keepalive` lets the request outlive a closing tab. */
  async flush(options: { keepalive?: boolean } = {}): Promise<void> {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
    while (this.queue.length > 0) {
      const events = this.queue.splice(0, this.maxBatch);
      try {
        const baseUrl = await this.discoveryApi.getBaseUrl('analytics');
        await this.fetchApi.fetch(`${baseUrl}/events`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ events }),
          keepalive: options.keepalive,
        });
      } catch {
        // Dropped on purpose: no retries, no errors surfaced to the user.
      }
    }
  }
}
